import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon, ChevronUpIcon } from '@/assets/icons';
import {
  Dropdown,
  DropdownMenuContent,
  DropdownMenuSimpleItem,
  DropdownMenuTrigger,
} from '@/components/organisms';
import { USER_MANAGEMENT_ROLE_CELL_TEST_IDS } from '@/constants/data-testids';
import { useUpdateUserRoleMutation } from '@/store/api/users';
import { USER_ROLE_OPTIONS, UserRole } from '@/types/users';
import { cn } from '@/utils/tailwind';

type UserManagementRoleCellProps = {
  userId: string;
  role: UserRole;
  canChangeRole?: boolean;
};

export const UserManagementRoleCell: FC<UserManagementRoleCellProps> = ({
  userId,
  role,
  canChangeRole = true,
}) => {
  const { t } = useTranslation('settings');
  const [updateUserRole, { isLoading: isUpdatingRole }] = useUpdateUserRoleMutation();
  const [hasUpdateError, setHasUpdateError] = useState(false);

  const label = t(`tabs.userManagement.roleDropdown.options.${role}`);

  const handleSelect = async (nextRole: UserRole) => {
    if (!canChangeRole || nextRole === role || isUpdatingRole) {
      return;
    }

    try {
      setHasUpdateError(false);
      await updateUserRole({ userId, role: nextRole }).unwrap();
    } catch (error) {
      setHasUpdateError(true);
      console.error('Failed to update user role', error);
    }
  };

  if (!canChangeRole) {
    return (
      <div className="min-w-0 max-w-full">
        <span
          data-testid={USER_MANAGEMENT_ROLE_CELL_TEST_IDS.TRIGGER}
          className="inline-flex max-w-full min-w-0 truncate text-body-14-regular text-custom-warm-grey-800"
        >
          {label}
        </span>
      </div>
    );
  }

  return (
    <div className="min-w-0 max-w-full">
      <Dropdown>
        <DropdownMenuTrigger
          variant="transparent"
          className={cn(
            'inline-flex max-w-full min-w-0 cursor-pointer items-center gap-1 rounded-md px-0 py-0.5',
            'text-body-14-regular text-custom-warm-grey-800',
            'hover:text-custom-warm-grey-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-custom-cyan-500/30'
          )}
          data-testid={USER_MANAGEMENT_ROLE_CELL_TEST_IDS.TRIGGER}
        >
          <span className="min-w-0 truncate">{label}</span>
          <ChevronUpIcon
            width={20}
            height={20}
            className="shrink-0 rotate-180 text-custom-warm-grey-500"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[200px]">
          {USER_ROLE_OPTIONS.map(option => (
            <DropdownMenuSimpleItem
              key={option}
              type="button"
              onClick={() => {
                void handleSelect(option);
              }}
            >
              <span className="flex w-full items-center justify-between gap-2">
                <span>{t(`tabs.userManagement.roleDropdown.options.${option}`)}</span>
                {option === role ? (
                  <CheckIcon width={16} height={16} className="text-custom-cyan-500" />
                ) : null}
              </span>
            </DropdownMenuSimpleItem>
          ))}
        </DropdownMenuContent>
      </Dropdown>
      {hasUpdateError ? (
        <p className="mt-1 text-body-12-regular text-custom-red-500">
          {t('tabs.userManagement.roleDropdown.updateErrorFallback', {
            defaultValue: 'Could not update role',
          })}
        </p>
      ) : null}
    </div>
  );
};
